import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

export type EppColumnSettingsEntry = {
  parameter: string;
  selected: boolean;
};

export type EppColumnSettingsDialogData = {
  title?: string;
  resolutionLabel?: string;
  entries: EppColumnSettingsEntry[];
  fixedColumns: string[];
};

export type EppColumnSettingsDialogResult = {
  entries: EppColumnSettingsEntry[];
};

@Component({
  selector: 'app-epp-column-settings-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    TranslateModule,
    FaIconComponent,
  ],
  templateUrl: './epp-column-settings-dialog.component.html',
  styleUrls: ['./epp-column-settings-dialog.component.scss'],
})
export class EppColumnSettingsDialogComponent {
  private dialogRef = inject(MatDialogRef<EppColumnSettingsDialogComponent>);
  private translate = inject(TranslateService);
  readonly data = inject<EppColumnSettingsDialogData>(MAT_DIALOG_DATA);

  readonly searchTerm = signal('');

  private readonly initialEntries: EppColumnSettingsEntry[] =
    this.data.entries.map(entry => ({ ...entry }));

  readonly allEntries = signal<EppColumnSettingsEntry[]>(
    this.data.entries.map(entry => ({ ...entry }))
  );

  readonly visibleEntries = computed<EppColumnSettingsEntry[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();

    if (term.length < 3) {
      return this.allEntries();
    }

    return this.allEntries().filter(entry => {
      const label = this.displayLabel(entry.parameter).toLowerCase();
      return label.includes(term);
    });
  });

  readonly selectableEntries = computed(() =>
    this.allEntries().filter(
      entry => !this.data.fixedColumns.includes(entry.parameter)
    )
  );

  readonly selectedCount = computed(
    () => this.selectableEntries().filter(entry => entry.selected).length
  );

  readonly selectableCount = computed(() => this.selectableEntries().length);

  private displayLabel(parameter: string): string {
    const key = `METPARA_EPP.${parameter}`;
    const translated = this.translate.instant(key);

    return translated && translated !== key
      ? translated
      : parameter.replace(/_/g, ' ');
  }

  onSelectionChange(event: MatSelectionListChange): void {
    const selectedEntry = event.options[0];
    if (!selectedEntry) return;

    this.toggle(selectedEntry.value, selectedEntry.selected);
  }

  toggle(parameter: string, selected: boolean): void {
    this.allEntries.update(list =>
      list.map(entry =>
        entry.parameter === parameter ? { ...entry, selected } : entry
      )
    );
  }

  readonly allSelected = computed(() => {
    const selectable = this.selectableEntries();
    return selectable.length > 0 && selectable.every(entry => entry.selected);
  });

  readonly someSelected = computed(() => {
    const selectable = this.selectableEntries();
    const selectedCount = selectable.filter(entry => entry.selected).length;
    return selectedCount > 0 && selectedCount < selectable.length;
  });

  toggleAll(checked: boolean): void {
    this.allEntries.update(list =>
      list.map(entry =>
        this.data.fixedColumns.includes(entry.parameter)
          ? entry
          : { ...entry, selected: checked }
      )
    );
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  resetDefaults(): void {
    this.allEntries.set(this.initialEntries.map(entry => ({ ...entry })));
    this.searchTerm.set('');
  }

  save(): void {
    this.dialogRef.close({
      entries: this.allEntries().map(entry => ({ ...entry })),
    } satisfies EppColumnSettingsDialogResult);
  }
}
